// Split-layout math: board panel on top, footage cropped into the bottom band
// of a 9:16 canvas — the classic chess Reels/Shorts composition. Pure and
// unit-agnostic: pass the real output frame (1080×1920) for export, or the
// on-screen frame box for the preview, and every number comes back in those
// units. Crop coordinates always refer to the source video's natural pixels.

const clamp = (v, min, max) => Math.min(max, Math.max(min, v))

export function splitGeometry({ frameW = 1080, frameH = 1920, share, videoW, videoH, pos = 0.5 }) {
  const regionH = Math.round(frameH * share)
  let boardPx = Math.min(frameW, regionH)
  boardPx -= boardPx % 2
  const boardX = Math.round((frameW - boardPx) / 2)
  const boardY = Math.round((regionH - boardPx) / 2)
  const bandH = frameH - regionH
  const videoBand = { w: frameW, h: bandH, y: regionH }

  // Crop the source to the band's aspect ratio; `pos` slides the crop window
  // along whichever axis overflows (0 = left/top, 1 = right/bottom).
  let crop = { w: videoW ?? 0, h: videoH ?? 0, x: 0, y: 0 }
  if (videoW > 0 && videoH > 0 && bandH > 0) {
    const bandAspect = videoBand.w / bandH
    const srcAspect = videoW / videoH
    if (srcAspect > bandAspect) {
      const w = Math.min(videoW, Math.round(videoH * bandAspect))
      crop = { w, h: videoH, x: clamp(Math.round((videoW - w) * pos), 0, videoW - w), y: 0 }
    } else {
      const h = Math.min(videoH, Math.round(videoW / bandAspect))
      crop = { w: videoW, h, x: 0, y: clamp(Math.round((videoH - h) * pos), 0, videoH - h) }
    }
  }

  return { frameW, frameH, share, regionH, boardPx, boardX, boardY, videoBand, crop }
}

const fmtNum = (n) => String(Math.round(n * 1000) / 1000)

// [0:v]crop,scale,setsar[vid];[vid]pad[base];[base][1:v]overlay…[out]
// Same still-PNG overlay chaining as the overlay-mode filter; the difference
// is that the composition target is a fresh padded canvas, not the source.
export function buildSplitFilterScript(segments, g, bg = 'black') {
  const head =
    `[0:v]crop=${g.crop.w}:${g.crop.h}:${g.crop.x}:${g.crop.y},` +
    `scale=${g.videoBand.w}:${g.videoBand.h},setsar=1[vid];` +
    `[vid]pad=${g.frameW}:${g.frameH}:0:${g.videoBand.y}:${bg}[base]`
  const chain = segments
    .map((seg, i) => {
      const src = i === 0 ? '[base]' : `[v${i}]`
      const outLabel = i === segments.length - 1 ? '[out]' : `[v${i + 1}]`
      const enable = `enable='between(t,${fmtNum(seg.start)},${fmtNum(seg.end)})'`
      return `${src}[${i + 1}:v]overlay=${g.boardX}:${g.boardY}:${enable}${outLabel}`
    })
    .join(';')
  return `${head};${chain}`
}
